'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Activity, CalendarClock, ChevronDown, Copy, Globe, History as HistoryIcon, Loader2, Maximize2, Minimize2, Pause, Pencil, Play, Plus, RotateCcw, Server, Trash2, XCircle } from 'lucide-react'
import { PageFrame, Module, ModuleFooter, StatusPill, Tag, Segmented, Fields, EmptyState, LoadingState, Toolbar, SearchInput, AttentionNote, Progress, useNotice, pillTone } from '../ui'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { PrivacyContext, PrivateColumnToggle, PrivateTd, usePrivateColumns } from '../privateColumns'
import { apiGet, apiPost, useCatalog } from './entriesApi'
import { SearchableSelect } from './SearchableSelect'
import { PlatformMark } from '../shell/PlatformMark'
import { STATUS_TONE, fmtAgo, fmtDuration, fmtLeft, isStale, jobLabel, verifyRunLabelOf, type JobRun, type JobRunStatus, type RunsResponse } from './jobTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'
import { useElementWidth } from '@/hooks/use-element-width'

// Operations › Jobs — the board the scheduled Python workers write to
// (job_worker heartbeats, job_run executions; fraud-dbs
// 20260912000000_tables_jobs.sql, jupyter-fraud libs/tooling/jobs.py).
// Editor only. Everything here is read from those tables; the only
// actions are queueing a manual run, pausing / resuming / cancelling one,
// expiring a browser handoff, and editing the schedules.
//
// Laid out as the design system's Jobs page (docs/ui-harmony-plan.md C5,
// the job-harmony mock): the heading with "Run a job", the metrics strip,
// then five numbered modules -- Workers, Running now, Browser handoffs,
// Schedules, History -- each a table or a list of rows that expand into a
// field list. "Run a job" and the schedule editor are dialogs.

type JobWorker = {
  worker: string
  hostname: string
  mode: string
  pid: number | null
  started_at: string
  last_seen_at: string
  version: string | null
  // workload: what the job would do if it fired now, evaluated by the
  // worker every few heartbeats ("42 candidates to analyse")
  // throughput: analyses per ten minutes this worker averaged over its runs
  // of that job in the last day -- carried on each schedule entry of the
  // job (same place as workload), so the row can say how fast the lane is
  jobs: Array<{ id: string; name: string; trigger: string; schedule_id?: string | null; next_run_at: string | null; workload?: { count: number | null; label: string; at: string } | null; throughput?: { per_10m: number; analyzed: number; runs: number; window_h: number; at: string } | null }>
  handles: string[]
  // per handled job, the parameters its function takes and their defaults
  // (read off the signature by the worker, fraud-dbs 20260916000000)
  param_specs?: Record<string, ParamSpec[]>
  // jobs this worker would handle but cannot on its machine, with the
  // setup checks that failed (WebKit missing...). They are left out of
  // handles, so their runs wait for a worker that can do them (fraud-dbs
  // 20261014000001, jupyter-fraud libs/tooling/worker_checks.py)
  unready?: Record<string, string[]>
  online: boolean
}

type ParamSpec = { name: string; default: unknown; required: boolean; type: string | null }

// One line of a schedule's *effective* parameters: what the row sets, and
// what the job fills in itself when the row is silent. platform_id is not
// a parameter of the row's JSON (it is the row's own column) so it is left
// out here.
type EffectiveParam = { name: string; value: unknown; explicit: boolean; required: boolean; missing: boolean }

function effectiveParams(spec: ParamSpec[] | undefined, params: Record<string, any> | null | undefined): EffectiveParam[] {
  const own = params ?? {}
  const out: EffectiveParam[] = []
  const seen = new Set<string>()
  for (const p of spec ?? []) {
    if (p.name === 'platform_id') continue
    seen.add(p.name)
    const explicit = Object.prototype.hasOwnProperty.call(own, p.name)
    out.push({ name: p.name, value: explicit ? own[p.name] : p.default, explicit, required: p.required, missing: p.required && !explicit })
  }
  for (const [k, v] of Object.entries(own)) {
    if (!seen.has(k)) out.push({ name: k, value: v, explicit: true, required: false, missing: false })
  }
  return out
}

// The row's params with every inherited default written in -- "explicit".
function explicitParams(spec: ParamSpec[] | undefined, params: Record<string, any> | null | undefined): Record<string, any> {
  const full: Record<string, any> = {}
  for (const p of spec ?? []) if (p.name !== 'platform_id' && !p.required) full[p.name] = p.default
  return { ...full, ...(params ?? {}) }
}

const fmtParam = (v: unknown) => (v === null || v === undefined ? 'null' : typeof v === 'string' ? v : JSON.stringify(v))

type JobSchedule = {
  id: string
  job_name: string
  platform_id: number | null
  cron: string
  params: Record<string, any>
  enabled: boolean
  label: string | null
  updated_by: string | null
  updated_at: string
  // the block: the job queued (trigger 'chain') once a firing of this row
  // succeeds, with these params -- how one row per brand sources and then
  // analyses on the lane it names
  then_job: string | null
  then_params: Record<string, any>
}

// The follow-ups the editor offers by name; any other handled job can be
// typed. The two analysis lanes are the whole point of the block.
const THEN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'then: nothing' },
  { value: 'auto_domains.analyze_5g', label: 'then: analyse on 5G (local)' },
  { value: 'auto_domains.analyze', label: 'then: analyse on Azure (proxied)' },
]
const shortJob = (name: string) => name.replace(/^auto_domains\./, '')

// The block's follow-up is an analyse lane: its then_params carry the
// monitor (decided 2026-09-17) -- every live candidate re-checked after
// monitor_live_days, every dead one after monitor_dead_days, a group off
// when its value is null. Since 2026-09-20 a dead candidate whose domain was
// registered within fresh_registration_days is re-checked after
// monitor_fresh_days instead: the lure registered yesterday and not up yet
// is the one that goes live tomorrow; a ten-year-old dead domain can wait a
// week. The editor shows these as typed controls; the raw JSON stays behind
// "Advanced" for any other job.
const isAnalyzeLane = (job: string) => /^auto_domains\.analyze/.test(job)

// What an analyse run is doing, for the running row: progress.step is the
// phase analyze_platform reports ('pending', 'monitor · live', 'monitor ·
// dead', 'verify', 'monitor' while it selects). Before it reports one --
// queued, or just claimed -- the scope it was asked for says what is coming.
function analyzePhase(r: JobRun): { label: string; tone: 'signal' | 'warning' | 'neutral'; title: string } | null {
  if (!isAnalyzeLane(r.job_name)) return null
  const step = r.progress?.step
  if (step && step !== 'claimed' && step !== 'selecting') {
    if (step.startsWith('monitor')) return { label: step, tone: 'warning', title: 'Re-checking candidates analysed before (the monitor)' }
    if (step === 'pending') return { label: 'pending', tone: 'signal', title: "Analysing candidates still 'sourced'" }
    return { label: step, tone: 'neutral', title: step }
  }
  if (Array.isArray(r.params?.candidate_ids) && r.params.candidate_ids.length) return { label: 'verify', tone: 'neutral', title: 'The candidates it was given' }
  // Every scope is pending candidates, then the monitor: since 2026-10-22
  // 'monitor' is an alias of 'pending' on the worker (jupyter-fraud
  // auto_domains_jobs module doc: ONE JOB) -- it used to skip the pending
  // candidates and say "nothing pending" over a brand's whole backlog.
  const scope = r.params?.scope ?? 'pending'
  return { label: 'pending + monitor', tone: 'signal', title: `scope=${scope}: pending candidates, then the monitor` }
}
const MONITOR_DEFAULTS = { monitor_live_days: 1, monitor_dead_days: 7, monitor_fresh_days: 1, fresh_registration_days: 30, auto_promote: false } as const
type MonitorDaysKey = 'monitor_live_days' | 'monitor_dead_days' | 'monitor_fresh_days' | 'fresh_registration_days'
function parseJsonObject(text: string): Record<string, any> | null {
  try {
    const v = text.trim() ? JSON.parse(text) : {}
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null
  } catch {
    return null
  }
}
// "new + monitor live 1 d / dead 7 d · auto-promote" for a block's row; a
// value the row does not set is the job's default, shown as such.
function monitorSummary(thenParams: Record<string, any> | null | undefined): Array<{ text: string; explicit: boolean }> {
  const p = thenParams ?? {}
  const has = (k: string) => Object.prototype.hasOwnProperty.call(p, k)
  const val = (k: MonitorDaysKey) => (has(k) ? p[k] : MONITOR_DEFAULTS[k])
  const days = (k: MonitorDaysKey, label: string) => {
    const v = val(k)
    return { text: v == null ? `${label} off` : `${label} ${v} d`, explicit: has(k) }
  }
  const promote = has('auto_promote') ? p.auto_promote : MONITOR_DEFAULTS.auto_promote
  const freshOn = val('monitor_fresh_days') != null && val('fresh_registration_days') != null
  return [
    { text: 'new', explicit: true },
    days('monitor_live_days', 'monitor live'),
    days('monitor_dead_days', 'dead'),
    { text: freshOn ? `fresh (< ${val('fresh_registration_days')} d old) ${val('monitor_fresh_days')} d` : 'fresh off',
      explicit: has('monitor_fresh_days') || has('fresh_registration_days') },
    { text: promote ? 'auto-promote' : 'no auto-promote', explicit: has('auto_promote') },
  ]
}

type HandoffStatus = 'pending' | 'claimed' | 'fulfilled' | 'failed' | 'expired'

// The browser half of a run (job_handoff): MerkleMap's UI is behind a
// Cloudflare check, so the searches are done by a Claude Code + Chrome
// session and handed back. Shown separately because a handoff outlives the
// job that parked it -- one whose job died is otherwise invisible.
type JobHandoff = {
  id: string
  kind: string
  status: HandoffStatus
  claimed_by: string | null
  error: string | null
  created_at: string
  claimed_at: string | null
  heartbeat_at: string | null
  fulfilled_at: string | null
  expires_at: string
  run_id: string | null
  brand: string | null
  job_run: { job_name: string; platform_id: number | null; status: string; progress: Record<string, any> | null } | null
}

const HANDOFF_STATUSES: HandoffStatus[] = ['pending', 'claimed', 'fulfilled', 'failed', 'expired']

// A blank schedule in the editor; `id` undefined means "insert".
type ScheduleDraft = { id?: string; job_name: string; platform_id: '' | number; cron: string; params: string; label: string; enabled: boolean; then_job: string; then_params: string }
const emptyDraft = (): ScheduleDraft => ({ job_name: '', platform_id: '', cron: '0 6 * * *', params: '{}', label: '', enabled: true, then_job: '', then_params: '{}' })

const STATUS_ORDER: JobRunStatus[] = ['queued', 'paused', 'running', 'succeeded', 'failed', 'cancelled']

// The handoffs table is hand-built; its Brand column still blurs in the
// sidebar's demo mode, as a ResizableTable's would (privateColumns.tsx).
const HANDOFF_COLUMNS = [
  { key: 'status', label: 'Status' }, { key: 'job', label: 'Job' }, { key: 'brand', label: 'Brand' },
  { key: 'searches', label: 'Searches' }, { key: 'claimed_by', label: 'Claimed by' }, { key: 'age', label: 'Age' },
  { key: 'expires', label: 'Expires' }, { key: 'actions', label: '' },
]

function fmtDate(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit' }).format(d)
  return `${time} ${date}`
}

// The run's status as a pill: stalled wins over running.
function runPill(r: JobRun) {
  const stale = isStale(r)
  return <StatusPill tone={stale ? 'danger' : pillTone(STATUS_TONE[r.status])}>{stale ? 'stalled' : r.status}</StatusPill>
}

const mono = 'font-mono text-label'
const selectCls = 'h-8 rounded-[2px] border border-border bg-background px-2 font-mono text-label text-foreground'
const fieldLabelCls = 'flex min-w-0 flex-col gap-1.5 text-nav font-medium'
const dialogSelectCls = 'h-10 w-full min-w-0 max-w-full rounded-[4px] border border-border bg-background px-2.5 text-nav font-normal text-foreground'
const dialogInputCls = 'h-10 rounded-[4px] border border-border bg-background px-2.5 font-mono text-label text-foreground placeholder:text-subtle focus:border-primary focus:outline-none'

export function JobsView({ theme }: { theme: ProcessedTheme }) {
  const { catalog } = useCatalog()
  const { notify } = useNotice()

  const [workers, setWorkers] = useState<JobWorker[]>([])
  const [open, setOpen] = useState<JobRun[]>([])
  const [names, setNames] = useState<string[]>([])
  const [runs, setRuns] = useState<JobRun[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The detail dialog follows a run by id, not by the row object clicked:
  // clicking stored a snapshot, so the tail and progress shown were frozen at
  // the moment of the click while the row underneath kept filling in.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = useMemo<JobRun | null>(
    () => (selectedId ? open.find((r) => r.id === selectedId) ?? runs.find((r) => r.id === selectedId) ?? null : null),
    [selectedId, open, runs],
  )

  const [handoffs, setHandoffs] = useState<JobHandoff[]>([])
  const [schedules, setSchedules] = useState<JobSchedule[]>([])
  const [draft, setDraft] = useState<ScheduleDraft | null>(null)
  const [thenAdvanced, setThenAdvanced] = useState(false)
  const [scheduleBusy, setScheduleBusy] = useState(false)
  const [scheduleNote, setScheduleNote] = useState<string | null>(null)

  // history filters
  const [jobFilter, setJobFilter] = useState('')
  const [statuses, setStatuses] = useState<JobRunStatus[]>([])
  const [triggerFilter, setTriggerFilter] = useState('')
  // A brand dashboard's jobs card links here with ?platform_id= so the
  // history opens already narrowed to that brand.
  const [platformFilter, setPlatformFilter] = useState<'' | number>(() => {
    if (typeof window === 'undefined') return ''
    const raw = new URLSearchParams(window.location.search).get('platform_id')
    const id = raw ? Number(raw) : NaN
    return Number.isFinite(id) ? id : ''
  })

  // schedules' own filters and the expanded rows
  const [schedSearch, setSchedSearch] = useState('')
  const [schedJobFilter, setSchedJobFilter] = useState('')
  const [schedPlatformFilter, setSchedPlatformFilter] = useState<'' | number>('')
  const [schedEnabledFilter, setSchedEnabledFilter] = useState<'' | 'enabled' | 'disabled'>('')
  const [openSchedules, setOpenSchedules] = useState<Set<string>>(() => new Set())
  const toggleScheduleOpen = (id: string) => setOpenSchedules((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })

  // handoffs' filters
  const [handoffSearch, setHandoffSearch] = useState('')
  const [handoffStatus, setHandoffStatus] = useState<'' | HandoffStatus>('')

  // the expanded rows of the other lists
  const [openRuns, setOpenRuns] = useState<Set<string>>(() => new Set())
  const toggleRunOpen = (id: string) => setOpenRuns((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const [openWorkers, setOpenWorkers] = useState<Set<string>>(() => new Set())
  const toggleWorkerOpen = (id: string) => setOpenWorkers((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })

  // "Run a job" dialog
  const [runOpen, setRunOpen] = useState(false)
  const [runJob, setRunJob] = useState('')
  const [runPlatform, setRunPlatform] = useState<'' | number>('')
  const [runParams, setRunParams] = useState('{}')
  // true once the operator typed in the params box: the box is theirs then,
  // and a job switch leaves it alone (with a note) instead of replacing it
  const [runParamsTyped, setRunParamsTyped] = useState(false)
  // the run's follow-up, like a schedule row's then_job: queued once it succeeds
  const [runThen, setRunThen] = useState('')
  const [runThenParams, setRunThenParams] = useState('{}')
  const [runBusy, setRunBusy] = useState(false)
  const [runNote, setRunNote] = useState<string | null>(null)

  const buildRunsParams = useCallback((targetPage: number) => {
    const p = new URLSearchParams({ page: String(targetPage) })
    if (jobFilter) p.set('job', jobFilter)
    if (statuses.length > 0) p.set('status', statuses.join(','))
    if (triggerFilter) p.set('trigger', triggerFilter)
    if (platformFilter !== '') p.set('platform_id', String(platformFilter))
    return p
  }, [jobFilter, statuses, triggerFilter, platformFilter])

  const fetchBoard = useCallback(async () => {
    const [w, o, n, sch, hof] = await Promise.all([
      apiGet<JobWorker[]>('/jobs/workers'),
      apiGet<JobRun[]>('/jobs/open'),
      apiGet<string[]>('/jobs/names'),
      apiGet<JobSchedule[]>('/jobs/schedules'),
      apiGet<JobHandoff[]>('/jobs/handoffs'),
    ])
    if (w.success && w.data) setWorkers(w.data)
    if (o.success && o.data) setOpen(o.data)
    if (n.success && n.data) {
      const list = [...n.data]
      if (list.includes('auto_domains.source') && !list.includes('auto_domains.source_antigravity')) {
        list.splice(list.indexOf('auto_domains.source') + 1, 0, 'auto_domains.source_antigravity')
      }
      setNames(list)
    }
    if (sch.success && sch.data) setSchedules(sch.data)
    if (hof.success && hof.data) setHandoffs(hof.data)
    const first = [w, o, n, sch, hof].find((r) => !r.success)
    if (first) setError(first.error ?? 'Failed to load the jobs board')
  }, [])

  const fetchRuns = useCallback(async (targetPage: number, isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else if (targetPage === 0) setLoading(true)
    setError(null)
    const r = await apiGet<RunsResponse>(`/jobs/runs?${buildRunsParams(targetPage).toString()}`)
    if (r.success && r.data) {
      setRuns((prev) => (targetPage === 0 ? r.data!.runs : [...prev, ...r.data!.runs]))
      setTotal(r.data.total)
      setHasMore(r.data.hasMore)
      setPage(targetPage)
      setLastUpdated(Date.now())
    } else {
      setError(r.error ?? 'Failed to load runs')
    }
    setLoading(false)
    setRefreshing(false)
  }, [buildRunsParams])

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchBoard(), fetchRuns(0, true)])
  }, [fetchBoard, fetchRuns])

  useEffect(() => { fetchBoard() }, [fetchBoard])
  useEffect(() => { fetchRuns(0) }, [fetchRuns])

  // The board is live state — poll it while the page is open so a run's
  // progress bar moves without touching Refresh. History only on demand.
  useEffect(() => {
    const t = setInterval(() => {
      fetchBoard()
      if (open.length > 0) fetchRuns(0, true)
    }, 15000)
    return () => clearInterval(t)
  }, [fetchBoard, fetchRuns, open.length])

  const running = useMemo(() => open.filter((r) => r.status === 'running'), [open])
  const queued = useMemo(() => open.filter((r) => r.status === 'queued'), [open])
  const paused = useMemo(() => open.filter((r) => r.status === 'paused'), [open])
  const failed24h = useMemo(() => {
    const since = Date.now() - 24 * 3600 * 1000
    return runs.filter((r) => r.status === 'failed' && (parseDbTimestamp(r.finished_at ?? r.queued_at)?.getTime() ?? 0) > since).length
  }, [runs])
  const platformName = useCallback((id: number | null) => {
    if (id == null) return null
    return catalog.platforms.find((p) => p.id === id)?.name_code ?? `#${id}`
  }, [catalog])
  // The brand with its mark (its registered logo, or its initial), by id or
  // by name_code (a handoff carries the brand as a code).
  const brandMark = useCallback((id: number | null, code?: string | null) => {
    const p = id != null ? catalog.platforms.find((x) => x.id === id) : code ? catalog.platforms.find((x) => x.name_code === code) : undefined
    const name = p?.name_code ?? code ?? (id != null ? `#${id}` : null)
    if (!name) return null
    return <span className="inline-flex items-center gap-1.5"><PlatformMark code={p?.name_code ?? name} label={p?.name ?? name} logoUrl={p?.logo_url} />{name}</span>
  }, [catalog])
  // The job's parameter signature as the workers publish it: an online
  // worker's copy first, any worker's otherwise.
  const specFor = useCallback((job: string): ParamSpec[] | undefined => {
    const ordered = [...workers].sort((a, b) => Number(b.online) - Number(a.online))
    for (const w of ordered) {
      const s = w.param_specs?.[job]
      if (s && s.length) return s
    }
    if (job === 'auto_domains.source_antigravity') {
      for (const w of ordered) {
        const s = w.param_specs?.['auto_domains.source']
        if (s && s.length) return s.map((p) => (p.name === 'client' ? { ...p, default: 'antigravity' } : p))
      }
    }
    return undefined
  }, [workers])

  const openRunDialog = (preset?: { job: string; platform: '' | number; params: Record<string, any>; then?: string | null; thenParams?: Record<string, any> }) => {
    if (preset) {
      setRunJob(preset.job); setRunPlatform(preset.platform); setRunParams(JSON.stringify(preset.params ?? {}, null, 2)); setRunParamsTyped(true)
      setRunThen(preset.then ?? ''); setRunThenParams(JSON.stringify(preset.thenParams ?? {}))
    } else {
      // A fresh "Run a job" starts from the job's own defaults. The box used
      // to keep whatever was typed in an earlier use of the dialog on this
      // page (2026-10-21: a {"scope": "monitor"} typed for one brand was
      // still there for the next brand's analyse, which then skipped its
      // 1224 pending candidates and reported "nothing pending"). A box the
      // operator typed in is theirs only within one use of the dialog.
      setRunParams(runJob ? JSON.stringify(explicitParams(specFor(runJob), {}), null, 2) : '{}')
      setRunParamsTyped(false)
      setRunThen(''); setRunThenParams('{}')
    }
    setRunNote(null)
    setRunOpen(true)
  }

  const queueRun = async () => {
    if (!runJob) return
    let params: Record<string, any> = {}
    let thenParams: Record<string, any> = {}
    try {
      params = runParams.trim() ? JSON.parse(runParams) : {}
      thenParams = runThen && runThenParams.trim() ? JSON.parse(runThenParams) : {}
    } catch {
      setRunNote('Params must be valid JSON')
      return
    }
    // The job's published signature is the contract: a key it does not take
    // is a wrong job's parameter set or a typo, never something to send.
    const spec = specFor(runJob)
    if (spec) {
      const known = new Set(spec.map((p) => p.name).concat(['platform_id']))
      const unknown = Object.keys(params).filter((k) => !known.has(k))
      if (unknown.length) {
        setRunNote(`${runJob} takes no parameter ${unknown.map((k) => `"${k}"`).join(', ')} — remove it (its parameters: ${spec.map((p) => p.name).join(', ')})`)
        return
      }
    }
    setRunBusy(true)
    setRunNote(null)
    // If an online worker handles auto_domains.source_antigravity directly,
    // queue it under that job_name; if the source worker has not restarted onto
    // the new registration yet, fall back to auto_domains.source with
    // client="antigravity" so it still runs on the Antigravity worker.
    const nativeAntigravity = runJob !== 'auto_domains.source_antigravity' || workers.some((w) => w.online && w.handles.includes('auto_domains.source_antigravity'))
    const effectiveJob = nativeAntigravity ? runJob : 'auto_domains.source'
    const effectiveParamsPayload = runJob === 'auto_domains.source_antigravity' ? { ...params, client: 'antigravity' } : params
    const r = await apiPost<JobRun>('/jobs/runs', {
      job_name: effectiveJob, platform_id: runPlatform === '' ? null : runPlatform, params: effectiveParamsPayload,
      ...(runThen ? { then_job: runThen, then_params: thenParams } : {}),
    })
    setRunBusy(false)
    if (r.success) {
      setRunOpen(false)
      notify(`Queued ${runJob}${runThen ? `, then ${runThen} once it succeeds` : ''}${runPlatform !== '' ? ` for ${platformName(runPlatform as number)}` : ''} — a worker that handles it picks it up within ~30 s`, { tone: 'success', ttl: 8000 })
      fetchBoard()
      fetchRuns(0, true)
    } else {
      setRunNote(String(r.error ?? 'Could not queue the run'))
    }
  }

  // Run a schedule's job now, with the schedule's own brand, params and
  // follow-up -- the same manual job_run "Run a job" queues.
  const [runningScheduleId, setRunningScheduleId] = useState<string | null>(null)
  const runSchedule = async (sch: JobSchedule) => {
    setRunningScheduleId(sch.id)
    const r = await apiPost<JobRun>('/jobs/runs', {
      job_name: sch.job_name, platform_id: sch.platform_id, params: sch.params ?? {},
      ...(sch.then_job ? { then_job: sch.then_job, then_params: sch.then_params ?? {} } : {}),
    })
    setRunningScheduleId(null)
    if (r.success) {
      notify(`Queued ${sch.job_name}${sch.then_job ? `, then ${sch.then_job} once it succeeds` : ''}${sch.platform_id != null ? ` for ${platformName(sch.platform_id)}` : ''} with this schedule's params — a worker picks it up within ~30 s`, { tone: 'success', ttl: 8000 })
      fetchBoard()
      fetchRuns(0, true)
    } else {
      notify(String(r.error ?? 'Could not queue the run'), { tone: 'warning' })
    }
  }

  // Run a finished run again, from its detail: the same job, brand and
  // params, as a new manual run. A schedule firing's row does not carry its
  // follow-up (the schedule does), so the block's then_job is taken from the
  // schedule -- a failed source re-run still chains the analysis.
  const [rerunBusy, setRerunBusy] = useState(false)
  const [rerunNote, setRerunNote] = useState<{ ok: boolean; text: string } | null>(null)
  useEffect(() => setRerunNote(null), [selectedId])
  const rerun = async (run: JobRun) => {
    const sch = run.trigger === 'schedule' && run.schedule_id ? schedules.find((s) => s.id === run.schedule_id) : undefined
    const thenJob = run.then_job ?? sch?.then_job ?? null
    const thenParams = run.then_job ? run.then_params : sch?.then_params
    setRerunBusy(true)
    setRerunNote(null)
    const r = await apiPost<JobRun>('/jobs/runs', {
      job_name: run.job_name, platform_id: run.platform_id ?? null, params: run.params ?? {},
      ...(thenJob ? { then_job: thenJob, then_params: thenParams ?? {} } : {}),
    })
    setRerunBusy(false)
    if (r.success && r.data) {
      setRerunNote({ ok: true, text: `Queued again as run ${r.data.id.slice(0, 8)}${thenJob ? `, then ${shortJob(thenJob)} once it succeeds` : ''} — a worker picks it up within ~30 s` })
      fetchBoard()
      fetchRuns(0, true)
    } else {
      setRerunNote({ ok: false, text: String(r.error ?? 'Could not queue the run again') })
    }
  }

  // Pause / resume one run (Hono setJobRunPaused). A queued run is never
  // claimed until resumed, and keeps its place in the queue. A running run
  // is asked to stand still at its next checkpoint, like Cancel.
  const [pausingId, setPausingId] = useState<string | null>(null)
  const setRunPaused = async (run: JobRun, pause: boolean) => {
    setPausingId(run.id)
    const r = await apiPost<JobRun>(`/jobs/runs/${run.id}/${pause ? 'pause' : 'resume'}`, {})
    setPausingId(null)
    if (!r.success) setError(String(r.error ?? `Could not ${pause ? 'pause' : 'resume'} the run`))
    fetchBoard()
    fetchRuns(0, true)
  }

  const cancelRun = async (run: JobRun) => {
    const r = await apiPost<JobRun>(`/jobs/runs/${run.id}/cancel`, {})
    if (!r.success) setError(String(r.error ?? 'Could not cancel the run'))
    fetchBoard()
    fetchRuns(0, true)
  }

  // The board answers "what is running right now", so offline workers are
  // hidden behind a count by default: a worker key carries the pod name, so
  // every Azure revision and restart leaves a row behind (swept after three
  // days by prune_job_workers).
  const [showOffline, setShowOffline] = useState(false)
  const offlineWorkers = useMemo(() => workers.filter((w) => !w.online), [workers])
  const onlineWorkers = useMemo(() => workers.filter((w) => w.online), [workers])
  const notReadyWorkers = useMemo(() => workers.filter((w) => w.online && Object.keys(w.unready ?? {}).length > 0).length, [workers])
  const visibleWorkers = useMemo(() => (showOffline ? workers : onlineWorkers), [workers, onlineWorkers, showOffline])

  const handledBy = useCallback((job: string) => {
    const direct = workers.filter((w) => w.online && w.handles.includes(job))
    if (direct.length === 0 && job === 'auto_domains.source_antigravity') {
      return workers.filter((w) => w.online && w.handles.includes('auto_domains.source'))
    }
    return direct
  }, [workers])
  // why nobody takes a job when an online worker would but for its machine
  const unreadyNote = useCallback((job: string) => {
    const w = workers.find((x) => x.online && x.unready?.[job]?.length)
    return w ? `${w.worker} is online but not ready: ${w.unready![job].join('; ')}` : null
  }, [workers])

  // next firing of a schedule, as the workers report it on their heartbeat
  const nextRunOf = useCallback((scheduleId: string) => {
    for (const w of workers) {
      if (!w.online) continue
      const j = w.jobs.find((x) => x.schedule_id === scheduleId)
      if (j) return { worker: w.worker, next: j.next_run_at, workload: j.workload ?? null, unready: w.unready ?? {} }
    }
    return null
  }, [workers])

  const saveSchedule = async () => {
    if (!draft) return
    let params: Record<string, any> = {}
    try {
      params = draft.params.trim() ? JSON.parse(draft.params) : {}
    } catch {
      setScheduleNote('Params must be valid JSON')
      return
    }
    let thenParams: Record<string, any> = {}
    try {
      thenParams = draft.then_params.trim() ? JSON.parse(draft.then_params) : {}
    } catch {
      setScheduleNote('Then-params must be valid JSON')
      return
    }
    setScheduleBusy(true)
    setScheduleNote(null)
    const r = await apiPost<JobSchedule>('/jobs/schedules', {
      id: draft.id, job_name: draft.job_name, platform_id: draft.platform_id === '' ? null : draft.platform_id,
      cron: draft.cron, params, label: draft.label || null, enabled: draft.enabled,
      then_job: draft.then_job || null, then_params: thenParams,
    })
    setScheduleBusy(false)
    if (r.success) {
      setDraft(null)
      notify('Schedule saved — online workers pick it up on their next heartbeat (~30 s)', { tone: 'success' })
      fetchBoard()
    } else {
      setScheduleNote(String(r.error ?? 'Could not save the schedule'))
    }
  }

  // Write the job's defaults onto the row for every parameter it leaves
  // out, so the schedule carries its whole configuration explicitly.
  const makeExplicit = async (sch: JobSchedule) => {
    const full = explicitParams(specFor(sch.job_name), sch.params)
    const r = await apiPost<JobSchedule>('/jobs/schedules', { ...sch, params: full })
    notify(r.success ? `${sch.job_name}: every parameter now written on the row` : String(r.error ?? 'Could not update the schedule'), { tone: r.success ? 'success' : 'warning' })
    fetchBoard()
  }

  const toggleSchedule = async (sch: JobSchedule) => {
    const r = await apiPost<JobSchedule>('/jobs/schedules', { ...sch, enabled: !sch.enabled })
    if (!r.success) notify(String(r.error ?? 'Could not update the schedule'), { tone: 'warning' })
    fetchBoard()
  }

  const deleteSchedule = async (sch: JobSchedule) => {
    const r = await apiPost(`/jobs/schedules/${sch.id}/delete`, {})
    if (!r.success) notify(String(r.error ?? 'Could not delete the schedule'), { tone: 'warning' })
    else notify('Schedule deleted', { tone: 'info' })
    fetchBoard()
  }

  const copySchedule = async (sch: JobSchedule) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ ...sch, params: explicitParams(specFor(sch.job_name), sch.params) }, null, 2))
      notify('Full schedule copied.', { tone: 'success' })
    } catch {
      notify('Clipboard unavailable. All fields remain in schedule details.', { tone: 'warning' })
    }
  }

  const openHandoffs = useMemo(() => handoffs.filter((h) => h.status === 'pending' || h.status === 'claimed'), [handoffs])
  const handoffPrivacy = usePrivateColumns(HANDOFF_COLUMNS)
  const shownHandoffs = useMemo(() => {
    const q = handoffSearch.trim().toLowerCase()
    return handoffs.filter((h) => (!handoffStatus || h.status === handoffStatus)
      && (!q || [h.job_run?.job_name, h.kind, h.brand, platformName(h.job_run?.platform_id ?? null), h.claimed_by, h.status].some((v) => (v ?? '').toLowerCase().includes(q))))
  }, [handoffs, handoffSearch, handoffStatus, platformName])

  const expireHandoff = async (h: JobHandoff) => {
    const r = await apiPost(`/jobs/handoffs/${h.id}/expire`, {})
    if (!r.success) setError(String(r.error ?? 'Could not expire the handoff'))
    fetchBoard()
  }

  const schedJobNames = useMemo(() => Array.from(new Set(schedules.map((s) => s.job_name))).sort(), [schedules])
  const shownSchedules = useMemo(() => {
    const q = schedSearch.trim().toLowerCase()
    return schedules.filter((s) =>
      (!schedJobFilter || s.job_name === schedJobFilter)
      && (schedPlatformFilter === '' || s.platform_id === schedPlatformFilter)
      && (!schedEnabledFilter || (schedEnabledFilter === 'enabled') === s.enabled)
      && (!q || [s.job_name, jobLabel(s.job_name), s.label, s.cron, platformName(s.platform_id), s.then_job, JSON.stringify(s.params ?? {}), s.updated_by].some((v) => (v ?? '').toLowerCase().includes(q))))
  }, [schedules, schedSearch, schedJobFilter, schedPlatformFilter, schedEnabledFilter, platformName])
  const allSchedulesOpen = shownSchedules.length > 0 && shownSchedules.every((s) => openSchedules.has(s.id))

  // ── schedule editor (a dialog) ─────────────────────────────────
  const draftOf = (sch: JobSchedule): ScheduleDraft => ({
    id: sch.id, job_name: sch.job_name, platform_id: sch.platform_id ?? '', cron: sch.cron, params: JSON.stringify(sch.params ?? {}, null, 2),
    label: sch.label ?? '', enabled: sch.enabled, then_job: sch.then_job ?? '', then_params: JSON.stringify(sch.then_params ?? {}),
  })
  const openDraft = (d: ScheduleDraft) => { setDraft(d); setThenAdvanced(false); setScheduleNote(null) }
  const closeDraft = () => { setDraft(null); setThenAdvanced(false) }

  const scheduleEditor = () => {
    if (!draft) return null
    const then = parseJsonObject(draft.then_params) ?? {}
    const setThen = (patch: Record<string, any>) => setDraft({ ...draft, then_params: JSON.stringify({ scope: 'pending', ...then, ...patch }) })
    const has = (k: string) => Object.prototype.hasOwnProperty.call(then, k)
    const numCls = 'h-7 w-16 rounded-[2px] border border-border bg-background px-1 text-right font-mono text-label disabled:opacity-40'
    const group = (k: MonitorDaysKey, label: string) => {
      const value = has(k) ? then[k] : MONITOR_DEFAULTS[k]
      const on = value != null
      return (
        <span className="inline-flex items-center gap-1.5 font-mono text-label text-foreground" data-monitor-group={k}>
          <input type="checkbox" checked={on} onChange={(e) => setThen({ [k]: e.target.checked ? (has(k) && then[k] != null ? then[k] : MONITOR_DEFAULTS[k]) : null })} title={`${label} candidates: re-check on / off (off is stored as null)`} />
          {label} every
          <input type="number" min={0.1} step={0.5} disabled={!on} value={on ? value : ''} onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n > 0) setThen({ [k]: n }) }}
            className={numCls} title="days since the last probe before a re-check; 0 is not a setting -- untick to stop the group" />
          d
        </span>
      )
    }
    const freshGroup = () => {
      const every = has('monitor_fresh_days') ? then.monitor_fresh_days : MONITOR_DEFAULTS.monitor_fresh_days
      const within = has('fresh_registration_days') ? then.fresh_registration_days : MONITOR_DEFAULTS.fresh_registration_days
      const on = every != null && within != null
      const num = (k: MonitorDaysKey, v: any, title: string) => (
        <input type="number" min={0.1} step={k === 'fresh_registration_days' ? 1 : 0.5} disabled={!on} value={on ? v : ''} onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n > 0) setThen({ [k]: n }) }}
          className={numCls} title={title} />
      )
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5 font-mono text-label text-foreground" data-monitor-group="fresh">
          <input type="checkbox" checked={on} onChange={(e) => setThen(e.target.checked
            ? { monitor_fresh_days: every ?? MONITOR_DEFAULTS.monitor_fresh_days, fresh_registration_days: within ?? MONITOR_DEFAULTS.fresh_registration_days }
            : { monitor_fresh_days: null, fresh_registration_days: null })} title="dead candidates on a freshly registered domain: re-check at their own cadence on / off (off: they wait the dead interval)" />
          fresh (registered &lt;
          {num('fresh_registration_days', within, 'a domain registered (WHOIS created; else first seen in certificate logs; unknown counts as fresh) within this many days is fresh')}
          d ago) every
          {num('monitor_fresh_days', every, 'days since the last probe before a fresh dead candidate is re-checked')}
          d
        </span>
      )
    }
    const blockEditor = draft.then_job && isAnalyzeLane(draft.then_job) && parseJsonObject(draft.then_params) != null && !thenAdvanced
    return (
      <div className="flex flex-col gap-4" data-schedule-editor>
        <label className={fieldLabelCls}>Job
          <select value={draft.job_name} onChange={(e) => {
            const job = e.target.value
            let current: Record<string, any> = {}
            try { current = draft.params.trim() ? JSON.parse(draft.params) : {} } catch { current = {} }
            // a fresh draft gets the job's full parameter set written out; an
            // edited one keeps what was typed and gains the missing defaults
            setDraft({ ...draft, job_name: job, params: JSON.stringify(explicitParams(specFor(job), current), null, 2) })
          }} className={dialogSelectCls}>
            <option value="">Job…</option>
            {names.map((n) => <option key={n} value={n}>{jobLabel(n)} · {n}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className={fieldLabelCls}>Platform
            <SearchableSelect theme={theme} value={draft.platform_id === '' ? undefined : draft.platform_id} options={catalog.platforms}
              placeholder="No platform" searchPlaceholder="Search platforms…" noneLabel="No platform"
              onChange={(id) => setDraft({ ...draft, platform_id: id ? Number(id) : '' })} />
          </label>
          <label className={fieldLabelCls}>Label
            <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="optional" className={dialogInputCls} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className={fieldLabelCls}>Cron expression · UTC
            <input value={draft.cron} onChange={(e) => setDraft({ ...draft, cron: e.target.value })} placeholder="min hour day month weekday" spellCheck={false} className={dialogInputCls} title="Five-field cron, UTC. e.g. '5 * * * *' hourly at :05, '30 6 * * *' daily 06:30" />
          </label>
          <label className={`${fieldLabelCls} justify-end`}>
            <span className="flex h-10 items-center gap-2 font-mono text-label font-normal text-muted-foreground"><Switch checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} /> {draft.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
        <label className={fieldLabelCls}>
          <span className="flex items-center justify-between">Parameters
            <button type="button" disabled={!draft.job_name} title="Write the job's defaults into the JSON for every parameter it leaves out" onClick={() => {
              let current: Record<string, any> = {}
              try { current = draft.params.trim() ? JSON.parse(draft.params) : {} } catch { setScheduleNote('Params must be valid JSON'); return }
              setDraft({ ...draft, params: JSON.stringify(explicitParams(specFor(draft.job_name), current), null, 2) })
            }} className="font-mono text-label font-normal uppercase tracking-widest text-subtle hover:text-signal disabled:opacity-40">Fill defaults</button>
          </span>
          <Textarea value={draft.params} onChange={(e) => setDraft({ ...draft, params: e.target.value })} placeholder='{"name_code": "ctt"}' spellCheck={false} rows={5} className="font-mono text-label" title="Parameters passed to the job on every firing (JSON object)" />
        </label>
        <label className={fieldLabelCls}>Then
          <select value={draft.then_job} data-field="then-job" onChange={(e) => setDraft({ ...draft, then_job: e.target.value })} className={dialogSelectCls} title="The block: the job queued (as a chained run) once a firing of this row succeeds -- which analysis lane follows the sourcing">
            {THEN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            {names.filter((n) => !THEN_OPTIONS.some((o) => o.value === n) && n !== draft.job_name).map((n) => <option key={n} value={n}>then: {n}</option>)}
          </select>
        </label>
        {blockEditor && (
          <div className="flex flex-col gap-2 rounded-[4px] bg-background p-3" data-block-editor>
            <label className="inline-flex items-center gap-1.5 font-mono text-label text-foreground">
              <input type="checkbox" checked={has('auto_promote') ? !!then.auto_promote : MONITOR_DEFAULTS.auto_promote} onChange={(e) => setThen({ auto_promote: e.target.checked })} title="Promote e33-confirmed impersonations of the brand without a click" />
              auto-promote
            </label>
            <span className="font-mono text-micro uppercase tracking-widest text-subtle" title="Continuous monitoring: every live candidate is re-checked after this many days, every dead one after that many -- except dead ones on a fresh registration, which get their own cadence; promoted and discarded never">monitor</span>
            {group('monitor_live_days', 'live')}
            {group('monitor_dead_days', 'dead')}
            {freshGroup()}
            <button type="button" onClick={() => setThenAdvanced(true)} className="self-start font-mono text-label uppercase tracking-widest text-subtle hover:text-signal" title="Edit the chained run's parameters as JSON">Advanced</button>
          </div>
        )}
        {draft.then_job && !blockEditor && (
          <label className={fieldLabelCls}>Then parameters
            <Textarea value={draft.then_params} onChange={(e) => setDraft({ ...draft, then_params: e.target.value })} placeholder='{"scope": "pending"}' spellCheck={false} rows={3} className="font-mono text-label" title="Parameters of the chained run (JSON object)" />
          </label>
        )}
        {scheduleNote && <AttentionNote tone="danger">{scheduleNote}</AttentionNote>}
      </div>
    )
  }

  // ── pieces of a schedule row ───────────────────────────────────
  const schedParams = (sch: JobSchedule) => {
    const eff = effectiveParams(specFor(sch.job_name), sch.params)
    if (eff.length === 0) return <span className="text-subtle">—</span>
    return (
      <pre className="m-0 whitespace-pre-wrap font-mono text-label [overflow-wrap:anywhere]">
        {eff.map((p, i) => (
          <span key={p.name} data-param={p.name} data-inherited={!p.explicit}
            title={p.missing ? 'required by the job and not set on the row' : p.explicit ? 'set on the row' : "inherited from the job's default; not written on the row"}
            className={p.missing ? 'text-destructive' : p.explicit ? 'text-foreground' : 'italic text-subtle'}>
            {i > 0 ? '\n' : ''}{p.name}={p.missing ? '?' : fmtParam(p.value)}
          </span>
        ))}
      </pre>
    )
  }
  const schedNext = (sch: JobSchedule, next: ReturnType<typeof nextRunOf>) => (
    !sch.enabled ? <span className="text-subtle">—</span> : next ? <>{fmtDate(next.next)}{next.unready[sch.job_name] && <span className="text-warning" title={next.unready[sch.job_name].join('; ')}> · not ready, the firing will fail</span>}</> : <span className="text-warning" title={unreadyNote(sch.job_name) ?? undefined}>{unreadyNote(sch.job_name) ? 'online worker not ready' : 'no online worker handles it'}</span>
  )
  const thenLine = (sch: JobSchedule) => sch.then_job ? (
    <span data-then-job={sch.then_job} title={`Queued once this firing succeeds, with ${JSON.stringify(sch.then_params ?? {})}`}>
      {sch.then_job}
      {isAnalyzeLane(sch.then_job) && (
        <span className="block" data-monitor-summary>
          {monitorSummary(sch.then_params).map((part, i) => (
            <span key={part.text} className={part.explicit ? '' : 'italic text-subtle'} title={part.explicit ? 'set on the row' : "the job's default; not written on the row"}>
              {i > 0 ? (i === 1 ? ' + ' : i === 2 ? ' / ' : ' · ') : ''}{part.text}
            </span>
          ))}
        </span>
      )}
    </span>
  ) : <span className="text-subtle">—</span>

  // History: on a narrow page the columns a glance needs; the rest on a wide one.
  const pageRef = useRef<HTMLDivElement>(null)
  const pageWidth = useElementWidth(pageRef)
  const wide = pageWidth >= 1180
  const COMPACT_HISTORY = new Set(['job', 'platform', 'status', 'started', 'duration', 'outcome'])
  const allColumns: ColumnDef[] = [
    { key: 'job', label: 'Job', initialWidth: 220 },
    { key: 'platform', label: 'Platform', initialWidth: 90 },
    { key: 'status', label: 'Status', initialWidth: 110 },
    { key: 'trigger', label: 'Trigger', initialWidth: 150, thTitle: 'schedule = fired by the worker\'s calendar · manual = queued from this page (by whom)' },
    { key: 'queued', label: 'Queued', initialWidth: 110 },
    { key: 'started', label: 'Started', initialWidth: 110 },
    { key: 'duration', label: 'Duration', initialWidth: 90 },
    { key: 'worker', label: 'Worker', initialWidth: 160 },
    { key: 'outcome', label: 'Outcome', initialWidth: 320 },
  ]
  const columns = wide ? allColumns : allColumns.filter((c) => COMPACT_HISTORY.has(c.key)).map((c) => (c.key === 'job' ? { ...c, initialWidth: 180 } : c.key === 'outcome' ? { ...c, initialWidth: 240 } : c))
  const col = (key: string) => wide || COMPACT_HISTORY.has(key)

  const metrics = [
    { label: 'Workers online', value: onlineWorkers.length, tone: 'default' as const },
    { label: 'Running now', value: running.length, tone: 'signal' as const },
    { label: 'Queue', value: queued.length + paused.length, tone: 'default' as const, note: paused.length ? `${paused.length} paused` : undefined },
    { label: 'Browser handoffs', value: openHandoffs.length, tone: 'default' as const, note: handoffs.length ? `${handoffs.length} records` : undefined },
    { label: 'Failed · 24h', value: failed24h, tone: failed24h > 0 ? 'danger' as const : 'default' as const },
  ]

  return (
    <PageFrame
      eyebrow="Operations"
      title="Jobs"
      rootRef={pageRef}
      info={
        <>
          <p>The Python workers (the scraping / monitoring containers) heartbeat <span className="font-mono">job_worker</span> every 30 s and record every execution in <span className="font-mono">job_run</span>. Nothing here talks to a container directly: <strong>Run a job</strong> queues a row that a worker handling that job claims on its next poll; <strong>Cancel</strong> sets a flag the job honours at its next checkpoint.</p>
          <p className="mt-1.5">A running row whose heartbeat is older than 2 minutes is shown as stalled; the workers&apos; own sweep marks it failed after 5. Schedules are cron in UTC.</p>
        </>
      }
      refresh={{ onRefresh: refreshAll, refreshing, lastUpdated, storageKey: 'jobs-header' }}
      primaryAction={<Button size="lg" onClick={() => openRunDialog()}><Play className="h-3.5 w-3.5" /> Run a job</Button>}
      metrics={metrics}
    >
      {error && <AttentionNote tone="danger">{error}</AttentionNote>}

      {/* ── 01 Workers ──────────────────────────────────────────── */}
      <Module
        title="Workers"
        icon={Server}
        storageKey="jobs:workers"
        summary={<span className="inline-flex items-center gap-1.5"><span className={`h-[5px] w-[5px] rounded-full ${onlineWorkers.length ? 'bg-success' : 'bg-destructive'}`} />{onlineWorkers.length} online{notReadyWorkers > 0 && <span className="text-warning"> · {notReadyWorkers} not ready for some jobs</span>}</span>}
        actions={offlineWorkers.length > 0 ? (
          <Button variant="action" size="xs" onClick={() => setShowOffline((v) => !v)}>{showOffline ? 'hide' : 'show'} {offlineWorkers.length} offline</Button>
        ) : undefined}
      >
        {workers.length === 0 ? (
          <EmptyState icon={Server} title="No worker has reported yet">Nothing is running the jobs board.</EmptyState>
        ) : visibleWorkers.length === 0 ? (
          <EmptyState icon={Server} title="No worker is online">{offlineWorkers.length} have reported in the past but none in the last minutes.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-small">
              <thead>
                <tr className="bg-sidebar-surface font-mono text-micro text-muted-foreground">
                  <th className="w-8 border-b border-border py-3 pl-3" />
                  <th className="border-b border-border px-4 py-3 font-[450]">Worker</th>
                  <th className="border-b border-border px-4 py-3 font-[450]">Role</th>
                  <th className="border-b border-border px-4 py-3 font-[450]">Heartbeat</th>
                  <th className="border-b border-border px-4 py-3 font-[450]">Version</th>
                  <th className="border-b border-border px-4 py-3 font-[450]">Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleWorkers.map((w) => {
                  const isOpen = openWorkers.has(w.worker)
                  const unready = Object.entries(w.unready ?? {})
                  const seen = new Set<string>()
                  const lanes = w.jobs.filter((j) => j.throughput && !seen.has(JSON.stringify(j.throughput)) && seen.add(JSON.stringify(j.throughput)))
                  return [
                    <tr key={w.worker} className="cursor-pointer hover:bg-secondary" onClick={() => toggleWorkerOpen(w.worker)} data-worker={w.worker}>
                      <td className="border-b border-border py-3 pl-3"><ChevronDown className={`h-3 w-3 text-subtle transition-transform ${isOpen ? '' : '-rotate-90'}`} /></td>
                      <td className={`border-b border-border px-4 py-3 ${mono} text-foreground`}>{w.worker}</td>
                      <td className="border-b border-border px-4 py-3 text-foreground">{w.mode || '—'}</td>
                      <td className={`border-b border-border px-4 py-3 ${mono} text-muted-foreground`}>{fmtAgo(w.last_seen_at)}</td>
                      <td className="border-b border-border px-4 py-3 text-muted-foreground">{w.version ?? '—'}</td>
                      <td className="border-b border-border px-4 py-3">
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <StatusPill tone={w.online ? 'success' : 'danger'}>{w.online ? 'Online' : 'Offline'}</StatusPill>
                          {w.online && unready.length > 0 && <StatusPill tone="warning" title={unready.map(([h, why]) => `${h}: ${why.join('; ')}`).join('\n')}>not ready · {unready.length}</StatusPill>}
                        </span>
                      </td>
                    </tr>,
                    isOpen ? (
                      <tr key={`${w.worker}-detail`}>
                        <td colSpan={6} className="border-b border-border bg-background px-5 py-3">
                          <Fields columns={2} fields={[
                            { label: 'Host', value: <span className={mono}>{w.hostname}</span> },
                            { label: 'Process', value: <span className={mono}>pid {w.pid ?? '—'} · up since {fmtDate(w.started_at)}</span> },
                            { label: 'Handles', value: w.handles.length ? <span className="flex flex-wrap gap-1">{w.handles.map((h) => <Tag key={h} title="Accepts manual runs of this job">{h}</Tag>)}</span> : '—' },
                            { label: 'Throughput', value: lanes.length ? <span className="flex flex-wrap gap-x-3">{lanes.map((j) => <span key={j.id} className={mono} data-throughput={j.throughput!.per_10m} title={`${j.throughput!.analyzed} analyses over ${j.throughput!.runs} run${j.throughput!.runs === 1 ? '' : 's'} in the last ${j.throughput!.window_h} h, measured ${fmtAgo(j.throughput!.at)}`}><span className="text-foreground">{j.throughput!.per_10m}</span> / 10 min</span>)}</span> : '—' },
                            ...(unready.length ? [{ label: 'Not ready', wide: true, value: <span className="flex flex-col gap-1">{unready.map(([h, why]) => <span key={h} data-unready-job={h} className={mono}><span className="text-warning">{h}</span> · {why.join('; ')}</span>)}</span> }] : []),
                            { label: 'Scheduled jobs', wide: true, value: w.jobs.length === 0 ? '—' : (
                              <table className="w-full font-mono text-label">
                                <tbody>
                                  {w.jobs.map((j) => (
                                    <tr key={j.id} className="border-t border-border first:border-t-0">
                                      <td className="py-1 pr-3 text-foreground" title={j.trigger}>{j.name}</td>
                                      <td className="py-1 pr-3 text-subtle">{j.trigger}</td>
                                      <td className="py-1 pr-3 whitespace-nowrap text-muted-foreground">next {fmtDate(j.next_run_at)}</td>
                                      <td className="py-1 text-muted-foreground">{j.workload?.label ?? ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) },
                          ]} />
                        </td>
                      </tr>
                    ) : null,
                  ]
                })}
              </tbody>
            </table>
          </div>
        )}
      </Module>

      {/* ── 02 Running now ─────────────────────────────────────── */}
      <Module
        title="Running now"
        icon={Activity}
        storageKey="jobs:running"
        summary={`${running.length} running · ${queued.length} queued${paused.length ? ` · ${paused.length} paused` : ''}`}
      >
        {open.length === 0 ? (
          <EmptyState icon={Activity} title="No running jobs.">Nothing running, nothing queued.</EmptyState>
        ) : open.map((r) => {
          const pct = r.progress?.total ? Math.min(100, Math.round(((r.progress.done ?? 0) / r.progress.total) * 100)) : null
          const stale = isStale(r)
          const holding = r.status === 'running' && r.pause_requested && !r.cancel_requested
          const held = r.status === 'paused' || holding
          const phase = analyzePhase(r)
          const isOpen = openRuns.has(r.id)
          const startedBy = r.trigger === 'manual' ? (r.requested_by ?? 'manual') : r.trigger === 'chain' ? `chain ← ${(r.parent_run_id ?? '').slice(0, 8) || '?'}` : 'schedule'
          const elapsed = r.status === 'running' ? fmtDuration(r.started_at) : `${r.status === 'paused' ? 'paused, queued' : 'queued'} ${fmtAgo(r.queued_at)}`
          const counts = pct != null ? `${r.progress!.done ?? 0}/${r.progress!.total}` : r.status === 'running' ? 'working' : ''
          const pace = phase && r.progress?.message
            ? r.progress.message
            : r.progress?.per_10m != null ? `${r.progress.per_10m} / 10 min${r.progress.eta_s ? ` · ~${fmtLeft(r.progress.eta_s)} left` : ''}` : null
          return (
            <div key={r.id} data-run-id={r.id} className={`relative border-b border-border px-5 py-5 last:border-b-0 before:absolute before:bottom-5 before:left-0 before:top-5 before:w-[2px] ${stale ? 'bg-danger-surface before:bg-destructive' : 'before:bg-signal'}`}>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => toggleRunOpen(r.id)} className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left">
                  <strong className="text-body font-medium text-foreground">{jobLabel(r.job_name)}</strong>
                  {r.platform_id != null && <span className="font-mono text-micro text-muted-foreground">{brandMark(r.platform_id)}</span>}
                  {runPill(r)}
                  {holding && <StatusPill tone="warning" title={r.paused_at ? `standing still since ${fmtAgo(r.paused_at)}` : 'holds at its next checkpoint'}>{r.paused_at ? 'paused' : 'pausing'}</StatusPill>}
                  {phase && <StatusPill tone={phase.tone} title={phase.title}>{phase.label}</StatusPill>}
                  {verifyRunLabelOf(r) && <Tag>{verifyRunLabelOf(r)}</Tag>}
                </button>
                <span className="shrink-0 font-mono text-micro text-muted-foreground">{elapsed}</span>
                <button type="button" onClick={() => toggleRunOpen(r.id)} aria-label={`Details for ${r.id.slice(0, 8)}`} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {r.status === 'running' && (
                <div className="mt-4" data-run-progress>
                  <Progress value={pct ?? 33} max={100} tone={stale ? 'danger' : 'signal'} label={counts} className={pct == null ? 'animate-pulse' : ''} />
                </div>
              )}
              {isOpen && (
                <div className="-mx-5 -mb-5 mt-4 border-t border-border bg-background px-5 py-3">
                  <Fields columns={2} fields={[
                    { label: 'Job', value: <code className={mono}>{r.job_name}</code> },
                    { label: 'Brand', value: brandMark(r.platform_id) ?? '—' },
                    { label: 'Scope', value: r.params?.scope ?? (phase ? phase.label : '—') },
                    { label: 'Started by', value: startedBy },
                    { label: 'Worker', value: <span className={mono}>{r.worker ?? 'unclaimed'}</span> },
                    { label: 'Elapsed', value: elapsed },
                    { label: 'Progress', value: counts || '—' },
                    { label: 'Pace', value: pace ?? '—' },
                    { label: 'Current domain / activity', value: r.progress?.current ?? '—', wide: true },
                    { label: 'Next activity', value: r.then_job ? `then ${shortJob(r.then_job)}` : phase?.label === 'pending + monitor' ? 'then the monitor' : '—', wide: true },
                  ]} />
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {!r.cancel_requested && (
                      <Button variant="action" size="sm" data-action={held ? 'run-resume' : 'run-pause'} disabled={pausingId === r.id} onClick={() => setRunPaused(r, !held)}
                        title={r.status === 'running'
                          ? (held ? 'Let it carry on from where it stands' : 'Hold it at its next checkpoint; it keeps its worker slot until resumed')
                          : (held ? 'Put it back in the queue, in its old place' : 'Hold it back: no worker picks it up until resumed')}>
                        {pausingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : held ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />} {held ? 'Resume' : 'Pause'}
                      </Button>
                    )}
                    {r.cancel_requested ? (
                      <span className="font-mono text-label text-muted-foreground">stopping…</span>
                    ) : (
                      <Button variant="action" size="sm" onClick={() => cancelRun(r)} title="Stop it at its next checkpoint"><XCircle className="h-3 w-3" /> Cancel job</Button>
                    )}
                    <Button variant="action" size="sm" onClick={() => setSelectedId(r.id)}>Open run</Button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </Module>

      {/* ── 03 Browser handoffs ────────────────────────────────── */}
      <Module
        title="Browser handoffs"
        icon={Globe}
        storageKey="jobs:handoffs"
        summary={`${openHandoffs.length} open · ${handoffs.length} records`}
      >
        {handoffs.length === 0 ? (
          <EmptyState icon={Globe} title="No handoffs yet">Searches a run parks for a Claude Code + Chrome session to answer.</EmptyState>
        ) : (
          <>
            <Toolbar end={
              <select value={handoffStatus} onChange={(e) => setHandoffStatus(e.target.value as '' | HandoffStatus)} aria-label="Handoff status" className={selectCls}>
                <option value="">All</option>
                {HANDOFF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            }>
              <SearchInput value={handoffSearch} onChange={setHandoffSearch} placeholder="Search handoffs…" />
            </Toolbar>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-small">
                <thead>
                  <tr className="bg-sidebar-surface font-mono text-micro text-muted-foreground">
                    {HANDOFF_COLUMNS.map((c, i) => (
                      <th key={c.key} className="whitespace-nowrap border-b border-border px-4 py-3 font-[450]">
                        <span className="flex items-center gap-1">{c.label}<PrivateColumnToggle colIdx={i} privacy={handoffPrivacy} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <PrivacyContext.Provider value={handoffPrivacy}>
                <tbody>
                  {shownHandoffs.length === 0 && <tr><td colSpan={8} className="px-4 py-4 font-mono text-label text-subtle">No handoff matches these filters.</td></tr>}
                  {shownHandoffs.map((h) => {
                    const p = h.job_run?.progress ?? null
                    const openRun = h.status === 'pending' || h.status === 'claimed'
                    return (
                      <tr key={h.id} className="hover:bg-secondary">
                        <td className="border-b border-border px-4 py-3"><StatusPill tone={pillTone(h.status)} title={h.error ?? undefined}>{h.status}</StatusPill></td>
                        <td className={`border-b border-border px-4 py-3 ${mono} text-foreground`}>{h.job_run?.job_name ?? <span className="text-subtle" title="the run that parked it is gone, or it was parked outside the board">{h.kind}</span>}</td>
                        <PrivateTd className={`border-b border-border px-4 py-3 ${mono} text-muted-foreground`}>{brandMark(h.job_run?.platform_id ?? null, h.brand) ?? '—'}</PrivateTd>
                        <td className="border-b border-border px-4 py-3 text-muted-foreground">{p?.total ?? '—'}</td>
                        <td className={`border-b border-border px-4 py-3 ${mono} text-muted-foreground`}>{h.claimed_by ?? <span className="text-warning">waiting for a browser</span>}</td>
                        <td className="whitespace-nowrap border-b border-border px-4 py-3 text-muted-foreground">{fmtAgo(h.created_at)}</td>
                        <td className={`whitespace-nowrap border-b border-border px-4 py-3 ${mono} text-muted-foreground`}>{h.status === 'fulfilled' ? fmtDate(h.fulfilled_at) : openRun ? fmtDate(h.expires_at) : '—'}</td>
                        <td className="whitespace-nowrap border-b border-border px-4 py-3 text-right">
                          {openRun && <Button variant="action" size="xs" onClick={() => expireHandoff(h)} title="Give up on this one: the waiting run stops waiting and records it">Expire</Button>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                </PrivacyContext.Provider>
              </table>
            </div>
            <ModuleFooter>{handoffs.length} records · {handoffs.filter((h) => h.status === 'pending').length} waiting for a browser</ModuleFooter>
          </>
        )}
      </Module>

      {/* ── 04 Schedules ───────────────────────────────────────── */}
      <Module
        title="Schedules"
        icon={CalendarClock}
        storageKey="jobs:schedules"
        summary={`${schedules.length} total · ${schedules.filter((s) => s.enabled).length} enabled`}
        actions={<>
          {schedules.length > 0 && (
            <Button variant="ghost" size="icon" aria-label={allSchedulesOpen ? 'Collapse all schedule details' : 'Expand all schedule details'} title={allSchedulesOpen ? 'Collapse all' : 'Expand all'}
              onClick={() => setOpenSchedules(allSchedulesOpen ? new Set() : new Set(shownSchedules.map((s) => s.id)))}>
              {allSchedulesOpen ? <Minimize2 /> : <Maximize2 />}
            </Button>
          )}
          <Button variant="action" size="sm" onClick={() => openDraft(emptyDraft())}><Plus className="h-3 w-3" /> Add</Button>
        </>}
      >
        {schedules.length === 0 ? (
          <EmptyState icon={CalendarClock} title="No schedules yet" action={<Button variant="outline" onClick={() => openDraft(emptyDraft())}><Plus className="h-3 w-3" /> Add schedule</Button>}>A worker seeds its defaults on first start, or add one here.</EmptyState>
        ) : (
          <>
            <Toolbar end={<>
              <select value={schedJobFilter} onChange={(e) => setSchedJobFilter(e.target.value)} data-filter="schedule-job" className={selectCls}>
                <option value="">All jobs</option>
                {schedJobNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="w-44">
                <SearchableSelect theme={theme} value={schedPlatformFilter === '' ? undefined : schedPlatformFilter} options={catalog.platforms}
                  placeholder="Any platform" searchPlaceholder="Search platforms…" noneLabel="Any platform"
                  onChange={(id) => setSchedPlatformFilter(id ? Number(id) : '')} />
              </div>
              <select value={schedEnabledFilter} onChange={(e) => setSchedEnabledFilter(e.target.value as '' | 'enabled' | 'disabled')} data-filter="schedule-enabled" aria-label="Schedule status" className={selectCls}>
                <option value="">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </>}>
              <SearchInput value={schedSearch} onChange={setSchedSearch} placeholder="Search all schedule fields…" inputProps={{ 'aria-label': 'Search schedules' }} />
            </Toolbar>
            <div data-schedules-layout="rows">
              {shownSchedules.length === 0 && <div className="px-5 py-4 font-mono text-label text-subtle">No schedule matches these filters.</div>}
              {shownSchedules.map((sch) => {
                const next = nextRunOf(sch.id)
                const isOpen = openSchedules.has(sch.id)
                const eff = effectiveParams(specFor(sch.job_name), sch.params)
                const inherited = eff.filter((p) => !p.explicit && !p.missing).length
                return (
                  <div key={sch.id} data-schedule-id={sch.id} className="border-b border-border last:border-b-0">
                    <div className={`grid min-h-[78px] grid-cols-[minmax(0,1fr)_110px_120px_32px] items-center gap-3 py-3 pl-2 pr-4 hover:bg-secondary ${sch.enabled ? '' : 'opacity-60'}`}>
                      <button type="button" onClick={() => toggleScheduleOpen(sch.id)} aria-label={`Details for schedule ${sch.id.slice(0, 8)}`} className="flex min-w-0 items-center gap-2 text-left">
                        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-subtle transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                        {sch.platform_id != null && <PlatformMark code={platformName(sch.platform_id) ?? ''} logoUrl={catalog.platforms.find((x) => x.id === sch.platform_id)?.logo_url} className="mr-1" />}
                        <span className="min-w-0">
                          <strong className="block text-nav font-medium text-foreground">{sch.label || `${platformName(sch.platform_id) ?? 'all'} · ${jobLabel(sch.job_name)}`}</strong>
                          <small className="mt-1 block font-mono text-micro text-subtle [overflow-wrap:anywhere]">{platformName(sch.platform_id) ?? '—'} · {sch.job_name}{sch.then_job ? ` → ${shortJob(sch.then_job)}` : ''}</small>
                        </span>
                      </button>
                      <span className="font-mono text-label"><small className="mb-1 block text-micro text-subtle">Next run</small>{schedNext(sch, next)}</span>
                      <label className="flex items-center gap-2 font-mono text-label text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                        <Switch checked={sch.enabled} onCheckedChange={() => toggleSchedule(sch)} aria-label={`${sch.enabled ? 'Disable' : 'Enable'} schedule ${sch.id.slice(0, 8)}`} />
                        {sch.enabled ? 'Enabled' : 'Disabled'}
                      </label>
                      <Button variant="ghost" size="icon" data-action="schedule-run-now" disabled={runningScheduleId != null} onClick={() => runSchedule(sch)} aria-label={`Run schedule ${sch.id.slice(0, 8)}`} title="Queue this job now with the schedule's brand, params and follow-up">
                        {runningScheduleId === sch.id ? <Loader2 className="animate-spin" /> : <Play />}
                      </Button>
                    </div>
                    {isOpen && (
                      <div className="border-t border-border bg-background px-5 py-3">
                        <Fields fields={[
                          { label: 'Job', value: <code className={mono}>{sch.job_name}</code> },
                          { label: 'Platform', value: brandMark(sch.platform_id) ?? '—' },
                          { label: 'Cron · UTC', value: <code className={mono}>{sch.cron}</code> },
                          { label: 'Label', value: sch.label ?? '—' },
                          { label: 'Parameters', value: schedParams(sch), wide: true },
                          { label: 'Inherited parameters', value: inherited > 0 ? `${inherited} inherited · the job's defaults, not written on the row` : 'none' },
                          { label: 'Then', value: thenLine(sch) },
                          { label: 'Next run', value: schedNext(sch, next) },
                          { label: 'Worker', value: next ? <span className={mono}>{next.worker}</span> : '—' },
                          { label: 'Workload', value: <span data-workload={next?.workload?.count ?? ''} title={next?.workload ? `measured ${fmtAgo(next.workload.at)}` : 'no online worker measures this job'}>{next?.workload ? next.workload.label : '—'}</span> },
                          { label: 'Updated', value: fmtAgo(sch.updated_at) },
                          { label: 'Updated by', value: sch.updated_by ? sch.updated_by.replace(/^seed:.*/, 'seed') : '—' },
                          { label: 'Status', value: sch.enabled ? 'Enabled' : 'Disabled' },
                          { label: 'Explicit parameters', value: inherited > 0 ? 'No' : 'Yes' },
                        ]} />
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <Button variant="action" size="sm" data-action="schedule-run-now" disabled={runningScheduleId != null} onClick={() => runSchedule(sch)}>{runningScheduleId === sch.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Run now</Button>
                          <Button variant="action" size="sm" data-action="schedule-edit" onClick={() => openDraft(draftOf(sch))}><Pencil className="h-3 w-3" /> Edit</Button>
                          <Button variant="action" size="sm" onClick={() => copySchedule(sch)}><Copy className="h-3 w-3" /> Copy</Button>
                          {inherited > 0 && <Button variant="action" size="sm" data-action="schedule-explicit" onClick={() => makeExplicit(sch)} title="Write every inherited default onto the row">Explicit</Button>}
                          <Button variant="action" size="sm" onClick={() => toggleSchedule(sch)}>{sch.enabled ? 'Disable' : 'Enable'}</Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteSchedule(sch)} aria-label={`Delete schedule ${sch.id.slice(0, 8)}`} title="Delete this schedule" className="text-destructive hover:text-destructive"><Trash2 /></Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <ModuleFooter>{schedules.length} schedules · {schedules.filter((s) => s.enabled).length} enabled · Workers apply changes on their next heartbeat</ModuleFooter>
          </>
        )}
      </Module>

      {/* ── 05 History ─────────────────────────────────────────── */}
      <Module
        title="History"
        icon={HistoryIcon}
        storageKey="jobs:history"
        summary={`${runs.length} of ${total ?? '—'} runs`}
      >
        <Toolbar end={
          <Segmented size="sm" multi value={statuses} onChange={(s) => setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))}
            segments={STATUS_ORDER.map((s) => ({ value: s, label: s }))} />
        }>
          <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} className={selectCls}>
            <option value="">All jobs</option>
            {names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <div className="w-44">
            <SearchableSelect theme={theme} value={platformFilter === '' ? undefined : platformFilter} options={catalog.platforms}
              placeholder="Any platform" searchPlaceholder="Search platforms…" noneLabel="Any platform"
              onChange={(id) => setPlatformFilter(id ? Number(id) : '')} />
          </div>
          <select value={triggerFilter} onChange={(e) => setTriggerFilter(e.target.value)} className={selectCls}>
            <option value="">Schedule + manual</option>
            <option value="schedule">Schedule only</option>
            <option value="manual">Manual only</option>
            <option value="chain">Chained only</option>
          </select>
        </Toolbar>
        {/* A plain block, not overflow-auto: a scroll container here captured
            the sticky headers (Jobs page bug, 2026-09-20). The ten-row box and
            its scrolling belong to ResizableTable's maxRows. */}
        <div>
          {loading ? (
            <LoadingState />
          ) : (
            <ResizableTable
              columns={columns}
              stickyHeader
              maxRows={10}
              theme={theme}
              isEmpty={runs.length === 0}
              emptyMessage="No runs match these filters"
            >
              {runs.map((r) => (
                <tr key={r.id} onClick={() => setSelectedId(r.id)} className="cursor-pointer border-b border-border transition-colors hover:bg-secondary">
                  <TableTd className="text-foreground"><span className="block">{jobLabel(r.job_name)}</span><span className={`block ${mono} text-subtle`}>{r.job_name}</span></TableTd>
                  <TableTd className={`${mono} text-muted-foreground`}>{brandMark(r.platform_id) ?? '—'}</TableTd>
                  <TableTd>{runPill(r)}</TableTd>
                  {col('trigger') && <TableTd className={`${mono} text-muted-foreground`} title={r.trigger === 'chain' ? `queued by run ${r.parent_run_id ?? '?'} when it succeeded` : undefined}>{r.trigger === 'manual' ? `${verifyRunLabelOf(r) ?? 'manual'} · ${r.requested_by ?? '?'}` : r.trigger === 'chain' ? `chain ← ${(r.parent_run_id ?? '').slice(0, 8) || '?'}` : 'schedule'}{r.then_job ? ` · then ${shortJob(r.then_job)}` : ''}</TableTd>}
                  {col('queued') && <TableTd className={`${mono} whitespace-nowrap text-muted-foreground`}>{fmtDate(r.queued_at)}</TableTd>}
                  <TableTd className={`${mono} whitespace-nowrap text-muted-foreground`}>{fmtDate(r.started_at)}</TableTd>
                  <TableTd className={`${mono} whitespace-nowrap text-muted-foreground`}>{r.started_at ? fmtDuration(r.started_at, r.finished_at) : '—'}</TableTd>
                  {col('worker') && <TableTd className={`${mono} truncate text-muted-foreground`} title={r.worker ?? undefined}>{r.worker ?? '—'}</TableTd>}
                  <TableTd className={`${mono} truncate ${r.error ? 'text-destructive' : 'text-muted-foreground'}`} title={r.error ?? (r.result ? JSON.stringify(r.result) : undefined)}>
                    {r.error ? r.error.split('\n')[0] : r.result ? JSON.stringify(r.result) : r.progress?.message ?? '—'}
                  </TableTd>
                </tr>
              ))}
            </ResizableTable>
          )}
          {hasMore && !loading && (
            <div className="flex justify-center border-t border-border py-3">
              <Button variant="action" size="sm" onClick={() => fetchRuns(page + 1)}>Load more</Button>
            </div>
          )}
        </div>
      </Module>

      {/* ── Run a job ──────────────────────────────────────────── */}
      <Dialog open={runOpen} onOpenChange={(o) => { setRunOpen(o); if (!o) setRunNote(null) }}>
        <DialogContent className="max-h-[85vh] max-w-[460px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Run a job</DialogTitle>
            <DialogDescription>Manual run · a worker that handles the job picks it up within ~30 s.</DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-4">
            <label className={fieldLabelCls}>Job
              <select value={runJob} onChange={(e) => {
                const job = e.target.value
                setRunJob(job)
                setRunNote(null)
                // The box follows the job: its full parameter set, so a manual
                // run is as explicit as a schedule row. A box the operator
                // typed in is theirs: kept, and said so.
                if (!runParamsTyped) setRunParams(job ? JSON.stringify(explicitParams(specFor(job), {}), null, 2) : '{}')
                else if (job) setRunNote(`Params box kept as you typed it — check it against ${job}'s parameters before queueing`)
              }} className={dialogSelectCls}>
                <option value="">Pick a job…</option>
                {names.map((n) => <option key={n} value={n}>{jobLabel(n)} · {n}</option>)}
              </select>
            </label>
            <label className={fieldLabelCls}>Platform
              <SearchableSelect theme={theme} value={runPlatform === '' ? undefined : runPlatform} options={catalog.platforms}
                placeholder="No platform" searchPlaceholder="Search platforms…" noneLabel="No platform"
                onChange={(id) => setRunPlatform(id ? Number(id) : '')} />
            </label>
            <label className={fieldLabelCls}>Parameters
              <Textarea value={runParams} onChange={(e) => { setRunParams(e.target.value); setRunParamsTyped(e.target.value.trim() !== '' && e.target.value.trim() !== '{}') }} placeholder='{"key": "value"}' spellCheck={false} rows={5} className="font-mono text-label" title="Extra parameters passed to the job as keyword arguments (JSON object)" />
            </label>
            <label className={fieldLabelCls}>Then
              <select value={runThen} data-field="run-then-job" onChange={(e) => setRunThen(e.target.value)} className={dialogSelectCls} title="Queued as a chained run once this one succeeds -- the same follow-up a schedule row has (e.g. source, then analyse)">
                {THEN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                {names.filter((n) => !THEN_OPTIONS.some((o) => o.value === n) && n !== runJob).map((n) => <option key={n} value={n}>then: {n}</option>)}
              </select>
            </label>
            {runThen && (
              <label className={fieldLabelCls}>Then parameters
                <Textarea value={runThenParams} onChange={(e) => setRunThenParams(e.target.value)} placeholder='{"scope": "pending"}' spellCheck={false} rows={2} className="font-mono text-label" title="Parameters of the chained run (JSON object)" />
              </label>
            )}
            {runJob && handledBy(runJob).length === 0 && (
              <AttentionNote>{unreadyNote(runJob) ? `${unreadyNote(runJob)} — ${runJob} will wait in the queue.` : `No online worker handles ${runJob} right now — it will wait in the queue.`}</AttentionNote>
            )}
            {runNote && <AttentionNote tone="danger">{runNote}</AttentionNote>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunOpen(false)}>Cancel</Button>
            <Button disabled={!runJob || runBusy} onClick={queueRun}>{runBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Start job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add / edit schedule ────────────────────────────────── */}
      <Dialog open={draft != null} onOpenChange={(o) => { if (!o) closeDraft() }}>
        <DialogContent className="max-h-[85vh] max-w-[520px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id ? 'Edit schedule' : 'Add schedule'}</DialogTitle>
            <DialogDescription>Cron in UTC · workers apply the change on their next heartbeat.</DialogDescription>
          </DialogHeader>
          {scheduleEditor()}
          <DialogFooter>
            <Button variant="outline" onClick={closeDraft}>Cancel</Button>
            <Button disabled={!draft?.job_name || !draft?.cron || scheduleBusy} onClick={saveSchedule}>{scheduleBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Run detail ─────────────────────────────────────────── */}
      <Dialog open={selected != null} onOpenChange={(o) => { if (!o) setSelectedId(null) }}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-3">{jobLabel(selected.job_name)}{selected.platform_id != null && <span className="font-mono text-label font-normal text-muted-foreground">{brandMark(selected.platform_id)}</span>}{runPill(selected)}</DialogTitle>
                <DialogDescription className="font-mono text-label">Run · {selected.id} · {selected.job_name}</DialogDescription>
              </DialogHeader>
              {/* Actions sit at the top: a failed run's error and log tail
                  can be long, and the footer was below the scroll. */}
              <div className="flex flex-wrap items-center justify-end gap-2">
                {(selected.status === 'failed' || selected.status === 'cancelled' || selected.status === 'succeeded') && (
                  <Button variant="outline" data-action="run-rerun" disabled={rerunBusy} onClick={() => rerun(selected)} title="Queue this job again with the same brand, params and follow-up">
                    {rerunBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Run again
                  </Button>
                )}
                <Button onClick={() => setSelectedId(null)}>Close</Button>
              </div>
              {rerunNote && <AttentionNote tone={rerunNote.ok ? 'info' : 'danger'}>{rerunNote.text}</AttentionNote>}
              <Fields columns={2} fields={[
                { label: 'Trigger', value: `${verifyRunLabelOf(selected) ?? selected.trigger}${selected.requested_by ? ` · ${selected.requested_by}` : ''}` },
                { label: 'Worker', value: <span className={mono}>{selected.worker ?? '—'}</span> },
                { label: 'Queued', value: fmtDate(selected.queued_at) },
                { label: 'Started', value: fmtDate(selected.started_at) },
                { label: 'Finished', value: fmtDate(selected.finished_at) },
                { label: 'Duration', value: selected.started_at ? fmtDuration(selected.started_at, selected.finished_at) : '—' },
                ...(Object.keys(selected.params ?? {}).length ? [{ label: 'Params', pre: true, value: JSON.stringify(selected.params, null, 2) }] : []),
                ...(selected.progress ? [{ label: 'Progress', pre: true, value: JSON.stringify(selected.progress, null, 2) }] : []),
                ...(selected.result ? [{ label: 'Result', pre: true, value: JSON.stringify(selected.result, null, 2) }] : []),
                ...(selected.error ? [{ label: <span className="text-destructive">Error</span>, pre: true, value: <span className="text-destructive">{selected.error}</span> }] : []),
                ...(selected.log_tail ? [{ label: 'Log tail', pre: true, value: selected.log_tail }] : []),
              ]} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageFrame>
  )
}
